class SafeLoadScripts extends HTMLElement {
  constructor() {
    super();
    this.isMobile = window.matchMedia('(max-width: 767px)').matches;
    this.loadedScripts = new Set();
  }

  connectedCallback() {
    const scripts = this.querySelectorAll('safe-load-scripts > script');
    
    if (this.isMobile) {
      // Mobile: Ultra-aggressive deferral for TBT optimization
      this.deferMobileScripts(scripts);
    } else {
      // Desktop: Standard loading
      this.loadScriptsStandard(scripts);
    }
  }

  deferMobileScripts(scripts) {
    // Mobile TBT optimization: Load scripts only when needed
    const criticalScripts = [];
    const deferredScripts = [];
    
    scripts.forEach((script) => {
      const key = script.dataset.flowLoadKey;
      const src = script.src;
      
      // Categorize scripts by priority
      if (this.isCriticalScript(key, src)) {
        criticalScripts.push(script);
      } else {
        deferredScripts.push(script);
      }
    });
    
    // Load critical scripts after a short delay
    if (criticalScripts.length > 0) {
      setTimeout(() => this.loadScriptBatch(criticalScripts), 100);
    }
    
    // Defer non-critical scripts until page is interactive
    if (deferredScripts.length > 0) {
      this.deferNonCriticalScripts(deferredScripts);
    }
  }
  
  isCriticalScript(key, src) {
    const criticalKeys = [
      'product-media',
      'product-information',
      'quantity-input'
    ];
    
    const criticalSrcPatterns = [
      'component-product-media.js',
      'component-product-information.js'
    ];
    
    return criticalKeys.includes(key) || 
           criticalSrcPatterns.some(pattern => src.includes(pattern));
  }
  
  deferNonCriticalScripts(scripts) {
    // Use different strategies for non-critical scripts
    const strategies = [
      { condition: () => 'requestIdleCallback' in window, method: this.loadOnIdle.bind(this) },
      { condition: () => true, method: this.loadOnIntersection.bind(this) }
    ];
    
    const strategy = strategies.find(s => s.condition());
    strategy.method(scripts);
  }
  
  loadOnIdle(scripts) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        this.loadScriptBatch(scripts);
      }, { timeout: 5000 });
    } else {
      setTimeout(() => this.loadScriptBatch(scripts), 2000);
    }
  }
  
  loadOnIntersection(scripts) {
    // Load scripts when related elements come into view
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadScriptBatch(scripts);
          observer.disconnect();
        }
      });
    }, { rootMargin: '100px' });
    
    // Observe potential trigger elements
    const triggerSelectors = [
      '[data-grid-slider-wrapper]',
      '.product-recommendations',
      '.complementary-products',
      '.cross-sell'
    ];
    
    let hasObservedElements = false;
    triggerSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        observer.observe(el);
        hasObservedElements = true;
      });
    });
    
    // Fallback if no trigger elements found
    if (!hasObservedElements) {
      setTimeout(() => this.loadScriptBatch(scripts), 3000);
    }
  }

  loadScriptsStandard(scripts) {
    // Desktop: Load scripts normally but still check for duplicates
    scripts.forEach((script) => {
      const key = script.dataset.flowLoadKey;
      this.loadSingleScript(script, key);
    });
  }
  
  loadScriptBatch(scripts) {
    // Load scripts in batches to prevent blocking
    const batchSize = this.isMobile ? 2 : 4;
    
    for (let i = 0; i < scripts.length; i += batchSize) {
      const batch = scripts.slice(i, i + batchSize);
      
      setTimeout(() => {
        batch.forEach(script => {
          const key = script.dataset.flowLoadKey;
          this.loadSingleScript(script, key);
        });
      }, i * 50); // Stagger batch loading
    }
  }
  
  loadSingleScript(script, key) {
    // Check if script already loaded globally or locally
    const scriptLoaded = window.wetheme?.webcomponentRegistry?.checkScriptLoaded?.(key) || 
                        this.loadedScripts.has(key);
                        
    if (!scriptLoaded) {
      const postLoadedScript = document.createElement('script');
      const { src, defer, async, type } = script;
      
      postLoadedScript.src = src;
      postLoadedScript.defer = defer;
      postLoadedScript.async = async;
      postLoadedScript.type = type;
      
      // Add loading optimization attributes
      if (this.isMobile) {
        postLoadedScript.setAttribute('importance', 'low');
      }
      
      // Track loaded scripts
      this.loadedScripts.add(key);
      
      // Register with theme registry if available
      if (window.wetheme?.webcomponentRegistry?.register) {
        window.wetheme.webcomponentRegistry.register({ key });
      }
      
      this.parentElement.appendChild(postLoadedScript);
    }
  }
}
  
customElements.define('safe-load-scripts', SafeLoadScripts);